import { FC, type KeyboardEvent, PropsWithChildren, ReactElement, useEffect, useRef } from 'react';
import { useCAWContext } from '../CAW';
import classnames from 'classnames';
import { Room, translate } from '@micro-apps/env';
import { PlanStore } from '@micro-apps/stores';
import Button from '../../button';
import { IconCallAltInverted, IconPlus, IconX } from '../../icon';

type Props = {
	onClose?: () => unknown;
	pause?: () => unknown;
	resume?: () => unknown;
};

type RoomOptionsDesktopTabBoundaryOptions = {
	isDesktop: boolean;
	onClose?: () => unknown;
};

export function handleRoomOptionsDesktopTabBoundary(
	event: KeyboardEvent<HTMLDivElement>,
	{ isDesktop, onClose }: RoomOptionsDesktopTabBoundaryOptions
): void {
	if (!isDesktop || event.key !== 'Tab') {
		return;
	}

	const container = event.currentTarget;
	const focusableSelector = [
		'button:not([disabled])',
		'[href]',
		'input:not([disabled])',
		'select:not([disabled])',
		'textarea:not([disabled])',
		'[tabindex]:not([tabindex="-1"])',
	].join(',');
	const focusableElements = Array.from(
		container.querySelectorAll<HTMLElement>(focusableSelector)
	).filter(element => {
		const ariaDisabled = element.getAttribute('aria-disabled') === 'true';
		return !ariaDisabled;
	});

	if (!focusableElements.length) {
		return;
	}

	const firstFocusable = focusableElements[0];
	const lastFocusable = focusableElements[focusableElements.length - 1];
	const currentTarget = event.target as HTMLElement;
	const isLeavingForwards = !event.shiftKey && currentTarget === lastFocusable;
	const isLeavingBackwards = event.shiftKey && currentTarget === firstFocusable;

	if (isLeavingForwards || isLeavingBackwards) {
		event.preventDefault();
		onClose?.();
	}
}

const RoomOptionLabel = ({
	planStore,
	labelType,
}: {
	planStore?: PlanStore;
	labelType: 'adult' | 'child';
}) => {
	if (!planStore) return null;

	return (
		<div
			className={classnames(
				{
					'RoomOption-adultstr': labelType === 'adult',
					'RoomOption-childstr': labelType === 'child',
				},
				'min-w-[120px] text-caption'
			)}
		>
			{labelType === 'adult' ? (
				<div className="flex flex-col">
					<span className="text-text-primary normal-case">{translate('Adults')}</span>
					{planStore?.config.maxChildAge ? (
						<span className="RoomOption-age text-text-secondary normal-case">
							{translate('{0}+ yrs', [(planStore?.config.maxChildAge + 1).toString()])}
						</span>
					) : null}
				</div>
			) : labelType === 'child' ? (
				<div className="flex flex-col">
					<span className="text-text-primary normal-case">{translate('Children')}</span>
					{planStore?.config.minChildAge && planStore?.config.maxChildAge ? (
						<span className="RoomOption-age text-text-secondary normal-case">
							{translate('{0} - {1} yrs', [
								planStore?.config.minChildAge.toString(),
								planStore?.config.maxChildAge.toString(),
							])}
						</span>
					) : null}
				</div>
			) : null}
		</div>
	);
};

const RoomOptionContainer = ({
	children,
	LabelComponent,
	containerType,
}: PropsWithChildren<{ LabelComponent: FC; containerType: 'adult' | 'child' }>) => {
	'className="RoomOption-child flex items-center justify-between" role="group"';
	return (
		<div
			className={classnames(
				{
					'RoomOption-child': containerType === 'child',
					'RoomOption-adult': containerType === 'adult',
				},
				'flex flex-row items-center justify-between spacing-inline-l'
			)}
			role="group"
		>
			<LabelComponent />
			<div className="RoomOption-controls flex flex-row w-[100px] justify-between items-end">
				{children}
			</div>
		</div>
	);
};

const ModifyGuestAmountButton = ({
	isDisabled,
	ariaLabel,
	onClick,
	buttonType,
}: {
	isDisabled: boolean;
	ariaLabel: string;
	onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
	buttonType: 'decrement' | 'increment';
}) => {
	return (
		<button
			className={classnames(
				{
					'button-disabled': isDisabled,
					'decrement-button': buttonType === 'decrement',
					'increment-button': buttonType === 'increment',
				},
				'text-center flex items-center justify-center h-[30px] min-h-[30px] w-[30px] min-w-[30px] items-center justify-center !border !border-border-strong !bg-transparent !text-text-primary flex-none shrink-0 grow-0 rounded-full',
				{
					'opacity-30': isDisabled,
				}
			)}
			aria-label={ariaLabel}
			tabIndex={0}
			aria-disabled={isDisabled}
			onClick={onClick}
		>
			<span aria-hidden="true" className="w-full text-center text-[16px] leading-none">
				{buttonType === 'decrement' ? '−' : '+'}
			</span>
		</button>
	);
};

export default function RoomOptions({ onClose, pause, resume }: Props): ReactElement<any> {
	const updateButton = useRef<HTMLButtonElement>(null);
	const newRoomOptionRef = useRef<HTMLDivElement | null>(null);
	const previousRoomsLengthRef = useRef(0);
	const shouldFocusNewRoomRef = useRef(false);
	const { planStore, isDesktop } = useCAWContext();
	const roomsLength = planStore?.search.rooms?.length ?? 0;

	useEffect(() => {
		const previousLength = previousRoomsLengthRef.current;
		if (shouldFocusNewRoomRef.current && roomsLength > previousLength) {
			newRoomOptionRef.current?.focus();
			const wrapperEl = document.querySelector('.Occupancy-wrapper');
			if (wrapperEl) {
				wrapperEl.scrollTop = 400;
			}
		}
		shouldFocusNewRoomRef.current = false;
		previousRoomsLengthRef.current = roomsLength;
	}, [roomsLength]);

	const renderRoom = (r: Room, i: number) => {
		const removeButtonDisabled = false;

		const isAdultPlusDisabled =
			r.adults >= (planStore?.config.maxAdults || 99) ||
			r.adults + r.children >= (planStore?.config.maxTotalOccupancy || 99);
		const isChildPlusDisabled =
			r.children >= (planStore?.config.maxChildren || 99) ||
			r.adults + r.children >= (planStore?.config.maxTotalOccupancy || 99);

		const isAdultMinusDisabled = r.adults <= 1;
		const isChildMinusDisabled = r.children <= 0;

		const roomOptionTitleText = `${translate('Room {0}', [(i + 1).toString()])}`;

		return (
			<div
				key={i}
				className="guests-dropdown-option flex flex-col gap-[20px]"
				role="row"
				tabIndex={0}
				aria-label={roomOptionTitleText}
				id={`occ-option-${i}`}
				aria-selected="true"
				ref={input => {
					if (i === roomsLength - 1) {
						newRoomOptionRef.current = input;
					}
				}}
			>
				<div className="flex flex-col gap-[15px] spacing-card-padding-x-xs">
					<div className="flex flex-row w-full justify-between">
						<div className="RoomOption-title text-label">
							<span className="text-text-primary">{roomOptionTitleText}</span>
						</div>
						{planStore?.search.rooms && (i > 0 || planStore?.search.rooms.length > 1) ? (
							<button
								key={`remove-room-${i}`}
								className={classnames('remove-button')}
								aria-label={translate(`Remove ${roomOptionTitleText}`)}
								disabled={removeButtonDisabled}
								onClick={() => {
									pause && pause();
									const rooms = [...(planStore?.search?.rooms ?? [])];
									rooms.splice(i, 1);
									planStore?.updateSearch({
										rooms,
									});
									setTimeout(() => {
										resume && resume();
										if (!document.activeElement || document.activeElement.localName === 'body') {
											updateButton?.current?.focus();
										}
									}, 1000);
								}}
							>
								<IconX height={14} width={14} />
							</button>
						) : null}
					</div>

					<div className="RoomOption-fields flex flex-col gap-[15px]">
						<RoomOptionContainer
							containerType="adult"
							LabelComponent={() => <RoomOptionLabel labelType="adult" planStore={planStore} />}
						>
							<ModifyGuestAmountButton
								key={`adult-minus-${i}`}
								buttonType="decrement"
								ariaLabel={translate(`Remove one adult from ${roomOptionTitleText}`)}
								isDisabled={isAdultMinusDisabled}
								onClick={e => {
									e.preventDefault();
									e.stopPropagation();
									if (isAdultMinusDisabled) {
										return;
									}
									const rooms = [...(planStore?.search?.rooms ?? [])];
									rooms[i].adults = rooms[i].adults - 1;
									planStore?.updateSearch({
										rooms,
									});
								}}
							/>
							<div className="text-body2">
								<span className="text-text-primary">{r.adults}</span>
							</div>
							<ModifyGuestAmountButton
								key={`adult-plus-${i}`}
								buttonType="increment"
								ariaLabel={translate(`Add one adult to ${roomOptionTitleText}`)}
								isDisabled={isAdultPlusDisabled}
								onClick={e => {
									e.preventDefault();
									e.stopPropagation();
									if (isAdultPlusDisabled) {
										return;
									}
									const rooms = [...(planStore?.search?.rooms ?? [])];
									rooms[i].adults = rooms[i].adults + 1;
									planStore?.updateSearch({
										rooms,
									});
								}}
							/>
						</RoomOptionContainer>
						<RoomOptionContainer
							LabelComponent={() => <RoomOptionLabel labelType="child" planStore={planStore} />}
							containerType="child"
						>
							<ModifyGuestAmountButton
								key={`child-minus-${i}`}
								buttonType="decrement"
								ariaLabel={translate(`Remove one child from ${roomOptionTitleText}`)}
								isDisabled={isChildMinusDisabled}
								onClick={e => {
									e.preventDefault();
									e.stopPropagation();
									if (isChildMinusDisabled) {
										return;
									}
									const rooms = [...(planStore?.search?.rooms ?? [])];
									rooms[i].children = rooms[i].children - 1;
									planStore?.updateSearch({
										rooms,
									});
								}}
							/>
							<div className="text-body2">
								<span className="text-text-primary">{r.children}</span>
							</div>
							<ModifyGuestAmountButton
								key={`child-plus-${i}`}
								buttonType="increment"
								ariaLabel={translate(`Add one child to ${roomOptionTitleText}`)}
								isDisabled={isChildPlusDisabled}
								onClick={e => {
									e.preventDefault();
									e.stopPropagation();
									if (isChildPlusDisabled) {
										return;
									}
									const rooms = [...(planStore?.search?.rooms ?? [])];
									rooms[i].children = rooms[i].children + 1;
									planStore?.updateSearch({
										rooms,
									});
								}}
							/>
						</RoomOptionContainer>
					</div>
				</div>
				{i !== (planStore?.search.rooms?.length ?? 0) - 1 ? (
					<div className="w-full h-[1px] bg-border-subtle"></div>
				) : null}
			</div>
		);
	};
	const renderContactOption = () => {
		const propertyPhone =
			planStore?.destination?.bookingConfig?.reservationPhone?.plainText ||
			planStore?.destination?.phoneNumberPrimary;
		const globalTollFreeLink =
			planStore?.regions?.[0]?.properties?.[0]?.contacts?.globalTollFreeLink ||
			'/content/fshr/pages/en/global-toll-free-telephone-numbers';
		const maxRooms = planStore?.config?.maxRooms?.toString();
		if (!propertyPhone && !globalTollFreeLink) {
			return null;
		}

		let contactOptionLink = '';
		if (propertyPhone) {
			const phoneHref = `tel:${propertyPhone.replace(/\D+/g, '')}`;
			contactOptionLink = `<a href="${phoneHref}">${propertyPhone}</a>`;
		} else {
			contactOptionLink = `<a href="${globalTollFreeLink}" target="_blank">${translate(
				'Toll Free Telephone Numbers By Country'
			)}</a>`;
		}
		let contactOptionLabel = translate(
			`To add more than {0} rooms, please call <span class='underline'>{1}</span>.`,
			[maxRooms || '4', contactOptionLink]
		).trimEnd(); // Trim ending spaces and periods
		if (contactOptionLabel.endsWith('.')) {
			contactOptionLabel = contactOptionLabel.substring(0, contactOptionLabel.length - 1);
		}
		return (
			<div
				className={classnames(
					'roomoptions-contact flex flex-row w-full spacing-card-padding-x-xs spacing-inline-xs'
				)}
			>
				<div
					className={classnames(
						'roomoptions-contact-icon inline-flex h-[20px] min-h-[20px] w-[20px] min-w-[20px] items-center justify-center text-[12px]'
					)}
					aria-hidden="true"
				>
					<IconCallAltInverted height={24} width={24} />
				</div>
				<div className="text-caption">
					<div className="text-[14px]" dangerouslySetInnerHTML={{ __html: contactOptionLabel }} />
				</div>
			</div>
		);
	};

	return (
		<div
			className="RoomOption-container flex flex-col gap-[20px] pt-[25px] pb-[40px] lg:pt-[30px] lg:pb-[20px]"
			role="application"
			onKeyDown={e => handleRoomOptionsDesktopTabBoundary(e, { isDesktop: !!isDesktop, onClose })}
		>
			{planStore?.search.rooms && planStore?.search.rooms.map(renderRoom)}
			<div className="w-full h-[1px] bg-border-subtle"></div>
			{planStore?.search.rooms &&
			planStore?.search.rooms.length < (planStore?.config.maxRooms || 99) ? (
				<Button
					key="add-room"
					intent="unstyled"
					size="small"
					icon={<IconPlus height={15} width={15} fill="currentColor" />}
					iconPosition="start"
					className="roomoptions-add-button !w-fit self-start text-button-primary text-button2 spacing-card-padding-x-xs pr-0 gap-[10px]"
					aria-label={translate('Add another room')}
					onClick={e => {
						e.preventDefault();
						e.stopPropagation();
						pause && pause();
						const rooms = [...(planStore?.search?.rooms ?? [])];

						shouldFocusNewRoomRef.current = true;
						rooms.push({
							adults: planStore?.config?.defaultAdults ?? 1,
							children: 0,
						});
						planStore?.updateSearch({
							rooms,
						});
						setTimeout(() => {
							resume && resume();
						}, 1000);
					}}
				>
					<div className="text-button-primary underline decoration-current decoration-solid underline-offset-[0.6em] decoration-[0.1em] [text-decoration-color:currentColor]">
						{translate('Add room')}
					</div>
				</Button>
			) : (
				<span>{renderContactOption()}</span>
			)}
		</div>
	);
}
